'use client';

/**
 * ReferralCapture - Silent referral code capture component
 *
 * Add this to auth pages (login, register, signup) to automatically
 * capture ?ref= params from URLs and store them for later checkout.
 *
 * This component:
 * 1. Captures the referral code from URL and stores in localStorage
 * 2. Tracks the click for analytics (conversion funnel)
 *
 * This component renders nothing - it just runs the capture logic on mount.
 *
 * Usage:
 * ```tsx
 * // In your auth page layout or page component:
 * import { ReferralCapture } from '@/features/referrals';
 *
 * export default function AuthLayout({ children }) {
 *   return (
 *     <>
 *       <ReferralCapture />
 *       {children}
 *     </>
 *   );
 * }
 * ```
 */

import { useEffect } from 'react';
import { captureReferralFromURL } from '../utils/referral-link';

/**
 * Track a referral click via API
 * Fire-and-forget - don't block on this
 */
async function trackClick(code: string): Promise<void> {
  try {
    await fetch('/api/referrals/track-click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch (_) {
    // Silently fail - tracking is non-critical
  }
}

export function ReferralCapture() {
  useEffect(() => {
    // Capture referral code from URL on mount
    const captured = captureReferralFromURL();
    if (captured) {
      // Track the click for analytics (fire and forget)
      trackClick(captured);
    }
  }, []);

  // This component renders nothing
  return null;
}
